import type { ViewerFieldInfo } from '@verclam/docx-kit-editor';

/** A field, or one instance of a list field, whose display flipped. `instancePath` is `null` outside lists. */
export type ShownChange = {
	readonly fieldId: string;
	readonly label: string;
	readonly instancePath: string | null;
};

export type ShownDiff = {
	readonly appeared: readonly ShownChange[];
	readonly disappeared: readonly ShownChange[];
};

const collectShown = (
	fields: readonly ViewerFieldInfo[],
): Map<string, { change: ShownChange; shown: boolean }> => {
	const shownByKey = new Map<string, { change: ShownChange; shown: boolean }>();

	for (const field of fields) {
		if (field.instances === undefined) {
			shownByKey.set(field.id, {
				change: { fieldId: field.id, label: field.label, instancePath: null },
				shown: field.shown,
			});
			continue;
		}

		for (const instance of field.instances) {
			shownByKey.set(`${field.id}|${instance.path}`, {
				change: { fieldId: field.id, label: field.label, instancePath: instance.path },
				shown: instance.shown,
			});
		}
	}

	return shownByKey;
};

/**
 * Compares two `handle.fields()` snapshots and lists what appeared or disappeared in the document. Only
 * what both snapshots know is compared: an element that did not exist before is a new element, not a
 * conditional passage.
 */
const diffShownFields = (
	before: readonly ViewerFieldInfo[],
	after: readonly ViewerFieldInfo[],
): ShownDiff => {
	const previous = collectShown(before);
	const appeared: ShownChange[] = [];
	const disappeared: ShownChange[] = [];

	for (const [key, current] of collectShown(after)) {
		const earlier = previous.get(key);

		if (earlier === undefined || earlier.shown === current.shown) {
			continue;
		}

		(current.shown ? appeared : disappeared).push(current.change);
	}

	return { appeared, disappeared };
};

export default diffShownFields;
